import React from "react";
import { useMappedState } from "redux-react-hook";
import styled, { css } from "styled-components";

import { IStore } from "src/core/shared/store";

import GraphLineChart from "./GraphLineChart";
import GraphBarChart from "./GraphBarChart";
import { GraphToolTipValueType } from "./GraphToolTip";
import { Text } from "../Text";

export interface GraphProps {
  data: object[];
  xAxis: GraphXAxis;
  graphMetrics: GraphMetric[];
  hideLegend?: boolean;
  toolTipValueType?: GraphToolTipValueType;
  type?: "bar" | "line";
  stacked?: boolean;
  containerWidth?: number;
}

export interface GraphXAxis {
  dataKey: string;
  dateFormat?: "day" | "month"
}

export interface GraphMetric {
  dataKey: string;
}

/**
 * Graph wrapper, funnels props and renders requested graph based on type,
 * defaulting to a line graph if not specified.
 */
const Graph: React.FunctionComponent<GraphProps> = ({
  type, data, ...rest
}) => {
  const { theme } = useMappedState(({ global }: IStore) => ({ theme: global.settings ? global.settings.theme : null }));

  if (!data.length) {
    return (
      <StyledWrapper>
        <div className="no-content">
          <Text weight="bold">
            No results found with those filters.
          </Text>
        </div>
      </StyledWrapper>
    );
  }

  if (type === "bar") {
    return (
      <GraphBarChart
        data={data}
        theme={theme}
        {...rest}
      />
    );
  }

  return (
    <GraphLineChart
      data={data}
      theme={theme}
      {...rest}
    />
  );
};

const StyledWrapper = styled.div`
  ${({ theme: _t }) => css`
    height: 100%;
    width: 100%;  
    
    .no-content {
      height: 100%;
      width: 100%;
      display: flex;
      justify-content: center;
      align-items: center;
      flex-direction: column;
    }
  `}
`;

export default Graph;